import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_URL, CP_API_URL } from "../../config/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Alert, AlertDescription } from "../../components/ui/alert";
import "../../styles/derma.css";

/**
 * Borang sumbangan untuk penyumbang yang datang daripada aplikasi mudah alih.
 *
 * Garis panduan App Store 3.2.2(iv) menghendaki kutipan derma berlaku DI LUAR
 * aplikasi, jadi aplikasi iOS membuka halaman ini dalam Safari dan bukan
 * memaparkan borang bayaran sendiri.
 *
 * Parameter ?h= ialah token serah-tangan jangka pendek. Ia ditukar dengan nama,
 * e-mel dan telefon penyumbang supaya mereka tidak perlu menaip semula apa yang
 * sudah ada dalam akaun mereka.
 *
 *
 * TOKEN TIDAK PERNAH WAJIB
 *
 * Ia mungkin tamat tempoh semasa pengguna berada di skrin ini, atau halaman ini
 * mungkin dibuka terus daripada pautan yang dikongsi. Dalam kedua-dua keadaan
 * borang masih berfungsi — ia hanya bermula kosong. Menjadikan token wajib
 * bermakna penyumbang yang berlengah beberapa minit kehilangan keupayaan
 * menderma sama sekali.
 */

const AMAUN_PANTAS = [10, 20, 50, 100];

function ringgit(n) {
    const v = Number(n);
    return Number.isFinite(v) ? v.toFixed(2) : "0.00";
}

const Checkout = () => {
    const { orgCode } = useParams();
    const [searchParams] = useSearchParams();
    const tokenHandoff = searchParams.get("h");

    const [memuat, setMemuat] = useState(true);
    const [ralatMuat, setRalatMuat] = useState(null);

    const [institusi, setInstitusi] = useState(null);
    const [senaraiTabung, setSenaraiTabung] = useState([]);
    const [amaunMinimum, setAmaunMinimum] = useState(2);

    const [tabungDipilih, setTabungDipilih] = useState(null);
    const [amaun, setAmaun] = useState("");
    const [nama, setNama] = useState("");
    const [emel, setEmel] = useState("");
    const [telefon, setTelefon] = useState("");
    const [accountId, setAccountId] = useState(null);

    const [menghantar, setMenghantar] = useState(false);
    const [ralatHantar, setRalatHantar] = useState(null);

    // Pangkalan API tanpa awalan /api — laluan /web berada pada akar.
    const asasWeb = useMemo(() => String(API_URL).replace(/\/api\/?$/, ""), []);

    useEffect(() => {
        let dibatalkan = false;

        const muat = async () => {
            try {
                // Ketiga-tiga permintaan bebas antara satu sama lain, jadi
                // menjalankannya berjujukan hanya menambah kelewatan pada
                // sambungan mudah alih.
                const [rInst, rKonfig, rHandoff] = await Promise.all([
                    fetch(`${asasWeb}/web/derma/institusi/${encodeURIComponent(orgCode)}`).then((r) => r.json()),
                    fetch(`${asasWeb}/web/derma/konfig`).then((r) => r.json()),
                    tokenHandoff
                        ? fetch(`${asasWeb}/web/derma/handoff/${encodeURIComponent(tokenHandoff)}`).then((r) => r.json())
                        : Promise.resolve(null),
                ]);

                if (dibatalkan) return;

                if (rInst?.status !== 200 || !rInst?.data) {
                    setRalatMuat(rInst?.message || "Institusi tidak dijumpai.");
                    return;
                }

                setInstitusi(rInst.data.institusi);
                setSenaraiTabung(rInst.data.tabung || []);

                const lalai = (rInst.data.tabung || []).find((t) => t.lalai) || (rInst.data.tabung || [])[0];
                if (lalai) setTabungDipilih(String(lalai.kod));

                if (rKonfig?.data?.amaun_minimum) setAmaunMinimum(Number(rKonfig.data.amaun_minimum));

                // Token yang gagal bukan ralat — borang hanya kekal kosong.
                if (rHandoff?.data) {
                    setNama(rHandoff.data.nama || "");
                    setEmel(rHandoff.data.emel || "");
                    setTelefon(rHandoff.data.telefon || "");
                    setAccountId(rHandoff.data.account_id ?? null);
                }
            } catch (e) {
                if (!dibatalkan) setRalatMuat("Tidak dapat menghubungi pelayan. Sila cuba lagi.");
            } finally {
                if (!dibatalkan) setMemuat(false);
            }
        };

        muat();
        return () => {
            dibatalkan = true;
        };
    }, [asasWeb, orgCode, tokenHandoff]);

    const amaunNombor = parseFloat(amaun);
    const amaunSah = Number.isFinite(amaunNombor) && amaunNombor >= amaunMinimum;
    const bolehHantar =
        amaunSah && tabungDipilih && nama.trim() && emel.trim() && telefon.trim() && !menghantar;

    const hantar = async () => {
        setRalatHantar(null);
        setMenghantar(true);

        try {
            const res = await fetch(`${CP_API_URL}/createPayment`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    orgCode,
                    tabungCode: tabungDipilih,
                    amount: amaunNombor.toFixed(2),
                    payorName: nama.trim(),
                    payorEmail: emel.trim(),
                    payorPhone: telefon.trim(),
                    paymentChannel: "1",
                    userId: accountId,
                }),
            });
            const json = await res.json();

            // Laluan ini memulangkan HTTP 200 walaupun untuk kegagalan, dengan
            // status sebenar dalam badan.
            if (json?.status === 200 && json?.data) {
                const asasGerbang =
                    process.env.REACT_APP_ENV === "production"
                        ? "https://toyyibpay.com/"
                        : "https://dev.toyyibpay.com/";
                window.location.href = asasGerbang + json.data;
                return;
            }

            setRalatHantar(json?.message || "Sumbangan tidak dapat diproses. Sila cuba lagi.");
        } catch (e) {
            setRalatHantar("Tidak dapat menghubungi pelayan. Sila semak sambungan internet anda.");
        } finally {
            setMenghantar(false);
        }
    };

    if (memuat) {
        return (
            <div className="derma-root">
                <div className="flex min-h-screen items-center justify-center px-6">
                    <p className="text-sm text-muted-foreground">Memuatkan…</p>
                </div>
            </div>
        );
    }

    if (ralatMuat) {
        return (
            <div className="derma-root">
                <div className="mx-auto flex min-h-screen max-w-md items-center px-6">
                    <Alert variant="destructive">
                        <AlertDescription>{ralatMuat}</AlertDescription>
                    </Alert>
                </div>
            </div>
        );
    }

    return (
        <div className="derma-root">
            <div className="mx-auto max-w-md px-5 pb-32 pt-8">
                <header className="mb-6 flex items-center gap-4">
                    {institusi?.imej ? (
                        <img
                            src={institusi.imej}
                            alt=""
                            className="h-14 w-14 shrink-0 rounded-xl border border-border object-cover"
                        />
                    ) : null}
                    <div className="min-w-0">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">Sumbangan kepada</p>
                        <h1 className="truncate text-lg font-semibold">{institusi?.nama}</h1>
                        {institusi?.bandar ? (
                            <p className="truncate text-sm text-muted-foreground">
                                {institusi.bandar}
                                {institusi.negeri ? `, ${institusi.negeri}` : ""}
                            </p>
                        ) : null}
                    </div>
                </header>

                <section className="mb-6">
                    <Label className="mb-3 block">Pilih tabung</Label>
                    {senaraiTabung.length === 0 ? (
                        <Alert variant="destructive">
                            <AlertDescription>
                                Institusi ini belum membuka sebarang tabung untuk sumbangan.
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <RadioGroup value={tabungDipilih ?? ""} onValueChange={setTabungDipilih} className="gap-2">
                            {senaraiTabung.map((t) => (
                                <label
                                    key={t.kod}
                                    htmlFor={`tabung-${t.kod}`}
                                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
                                        tabungDipilih === String(t.kod)
                                            ? "border-primary bg-primary/5"
                                            : "border-border"
                                    }`}
                                >
                                    <RadioGroupItem value={String(t.kod)} id={`tabung-${t.kod}`} className="mt-0.5" />
                                    <span className="min-w-0">
                                        <span className="block text-sm font-medium">{t.nama}</span>
                                        {t.keterangan ? (
                                            <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                                                {t.keterangan}
                                            </span>
                                        ) : null}
                                    </span>
                                </label>
                            ))}
                        </RadioGroup>
                    )}
                </section>

                <section className="mb-6">
                    <Label htmlFor="amaun" className="mb-3 block">
                        Jumlah sumbangan
                    </Label>

                    <div className="mb-3 grid grid-cols-4 gap-2">
                        {AMAUN_PANTAS.map((a) => (
                            <Button
                                key={a}
                                type="button"
                                variant={String(amaunNombor) === String(a) ? "default" : "outline"}
                                onClick={() => setAmaun(String(a))}
                                className="h-11 px-0 text-sm"
                            >
                                {a}
                            </Button>
                        ))}
                    </div>

                    <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                            RM
                        </span>
                        {/*
                          inputMode="decimal" memberi papan kekunci angka pada iOS
                          tanpa menolak nilai perpuluhan, yang type="number" lakukan
                          secara tidak konsisten merentas pelayar.
                        */}
                        <Input
                            id="amaun"
                            inputMode="decimal"
                            placeholder={ringgit(amaunMinimum)}
                            value={amaun}
                            onChange={(e) => setAmaun(e.target.value.replace(/[^0-9.]/g, ""))}
                            className="pl-11"
                        />
                    </div>

                    {amaun && !amaunSah ? (
                        <p className="mt-2 text-xs text-destructive">
                            Jumlah minimum ialah RM{ringgit(amaunMinimum)}.
                        </p>
                    ) : (
                        <p className="mt-2 text-xs text-muted-foreground">
                            Minimum RM{ringgit(amaunMinimum)}.
                        </p>
                    )}
                </section>

                <section className="mb-6">
                    <Label className="mb-3 block">Maklumat penyumbang</Label>
                    <Card>
                        <CardContent className="space-y-4 p-4">
                            <div>
                                <Label htmlFor="nama" className="mb-1.5 block text-xs text-muted-foreground">
                                    Nama
                                </Label>
                                <Input id="nama" value={nama} onChange={(e) => setNama(e.target.value)} />
                            </div>
                            <div>
                                <Label htmlFor="emel" className="mb-1.5 block text-xs text-muted-foreground">
                                    E-mel
                                </Label>
                                <Input
                                    id="emel"
                                    type="email"
                                    autoCapitalize="none"
                                    value={emel}
                                    onChange={(e) => setEmel(e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="telefon" className="mb-1.5 block text-xs text-muted-foreground">
                                    No. telefon
                                </Label>
                                <Input
                                    id="telefon"
                                    inputMode="tel"
                                    value={telefon}
                                    onChange={(e) => setTelefon(e.target.value)}
                                />
                            </div>
                        </CardContent>
                    </Card>
                    <p className="mt-2 text-xs text-muted-foreground">
                        Resit sumbangan akan dihantar ke e-mel ini.
                    </p>
                </section>

                {ralatHantar ? (
                    <Alert variant="destructive" className="mb-4">
                        <AlertDescription>{ralatHantar}</AlertDescription>
                    </Alert>
                ) : null}
            </div>

            {/*
              Butang melekat di bawah. Borang ini lebih panjang daripada satu
              skrin telefon, dan butang yang menunggu di hujung skrol bermakna
              penyumbang yang sudah bersedia membayar terpaksa mencarinya.
            */}
            <div className="fixed inset-x-0 bottom-0 border-t border-border bg-background/95 px-5 pb-6 pt-4 backdrop-blur">
                <div className="mx-auto max-w-md">
                    <Button size="lg" className="w-full" disabled={!bolehHantar} onClick={hantar}>
                        {menghantar ? "Memproses…" : `Sumbang RM${ringgit(amaunSah ? amaunNombor : amaunMinimum)}`}
                    </Button>
                    <p className="mt-2 text-center text-xs text-muted-foreground">
                        Bayaran diproses dengan selamat oleh ToyyibPay (FPX).
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
